import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Download, 
  X, 
  FileText,
  Calendar,
  List
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { startBackgroundExport } from '@/utils/organization/backgroundExport';
import { useExportNotifications } from '@/hooks/useExportNotifications';
import { ReportsExportList } from '@/components/organisation/ReportsExportList';

interface ReportsExportManagerProps {
  organisation: any;
  isOpen: boolean;
  onClose: () => void;
  activeReportTab: string;
  filters: any;
}

export const ReportsExportManager: React.FC<ReportsExportManagerProps> = ({
  organisation,
  isOpen,
  onClose,
  activeReportTab,
  filters
}) => {
  const [view, setView] = useState<'new' | 'list'>('new');
  const [exportSettings, setExportSettings] = useState({
    dataSource: '',
    format: 'csv',
    includeHeaders: true,
    filename: `${activeReportTab === 'skus' ? 'products' : activeReportTab}_report`
  });
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();
  
  // Enable export notifications for report exports
  useExportNotifications({ 
    enabled: isOpen, 
    includeReportExports: true 
  });

  // Update filename when data source changes
  React.useEffect(() => {
    const connectionType = getConnectionTypeFromDataSource(exportSettings.dataSource);
    const baseFilename = `${activeReportTab === 'skus' ? 'products' : activeReportTab}_report`;
    const newFilename = connectionType ? `${connectionType}_${baseFilename}` : baseFilename;
    
    setExportSettings(prev => ({ ...prev, filename: newFilename }));
  }, [exportSettings.dataSource, activeReportTab]);

  // Sync export source with the report's active dataSources filter
  React.useEffect(() => {
    const availableSources = getAvailableDataSources();
    if (availableSources.length === 0 || availableSources[0] === 'No supported connections') return;

    const activeDataSources = filters.dataSources || [];
    
    if (activeDataSources.length === 0) {
      // No filter active — pick first available
      const defaultSource = availableSources.find(s => s !== 'All') || availableSources[0];
      setExportSettings(prev => ({ ...prev, dataSource: defaultSource }));
      return;
    }

    // Check if all non-"All" sources are selected → use "All"
    const nonAllSources = availableSources.filter(s => s !== 'All');
    const allSelected = nonAllSources.length > 1 && activeDataSources.length >= nonAllSources.length;
    
    if (allSelected && availableSources.includes('All')) {
      setExportSettings(prev => ({ ...prev, dataSource: 'All' }));
      return;
    }

    // Single source selected — map connection ID back to display name
    if (activeDataSources.length === 1) {
      const connId = activeDataSources[0];
      const matchedName = mapConnectionIdToExportName(connId);
      if (matchedName && availableSources.includes(matchedName)) {
        setExportSettings(prev => ({ ...prev, dataSource: matchedName }));
        return;
      }
    }

    // Multiple (but not all) sources — default to first matched or "All" if available
    if (availableSources.includes('All')) {
      setExportSettings(prev => ({ ...prev, dataSource: 'All' }));
    } else {
      const defaultSource = availableSources.find(s => s !== 'All') || availableSources[0];
      setExportSettings(prev => ({ ...prev, dataSource: defaultSource }));
    }
  }, [organisation?.connections, filters.dataSources]);

  function mapConnectionIdToExportName(connectionId: string): string | null {
    if (!organisation?.connections) return null;
    const connections = Object.values(organisation.connections) as any[];
    const conn = connections.find((c: any) => (c.id || c.connectionId) === connectionId);
    if (!conn) return null;

    const type = conn.type?.toLowerCase() || '';
    const service = conn.service?.toLowerCase() || '';
    const ecommerceSubtype = conn.ecommerce_subtype?.toLowerCase() || '';
    const isCsv = service === 'csv' || type === 'csv';

    if (isCsv && conn.import_type === 'ecommerce') {
      if (ecommerceSubtype === 'amazon') return 'Amazon CSV Import';
      if (ecommerceSubtype === 'shopify') return 'Shopify CSV Import';
    }
    if (type.includes('amazon') || conn.service === 'amazon_selling_partner') return 'Amazon Selling Partner';
    if (type.includes('shopify')) return 'Shopify';
    return null;
  }

  function getConnectionTypeFromDataSource(dataSource: string) {
    if (!organisation?.connections) return null;
    if (dataSource === 'All') return null;
    
    // Direct mapping from the distinct source names
    const sourceMap: Record<string, string> = {
      'Amazon Selling Partner': 'amazon',
      'Amazon CSV Import': 'amazon',
      'Shopify': 'shopify',
      'Shopify CSV Import': 'shopify',
    };
    
    return sourceMap[dataSource] || null;
  }

  const getAvailableDataSources = () => {
    const sources: string[] = [];
    const supportedConnectors = ['shopify', 'amazon'];
    
    if (organisation?.connections) {
      const connections = Object.values(organisation.connections);
      
      connections.forEach((connection: any) => {
        const connectorName = connection?.connector_name?.toLowerCase() || '';
        const type = connection?.type?.toLowerCase() || '';
        const service = connection?.service?.toLowerCase() || '';
        const isCSV = service === 'csv' || type === 'csv';
        const ecommerceSubtype = connection?.ecommerce_subtype?.toLowerCase() || '';
        
        // Determine platform
        let platform = '';
        if (connectorName.includes('amazon') || type.includes('amazon') || ecommerceSubtype === 'amazon') {
          platform = 'amazon';
        } else if (connectorName.includes('shopify') || type.includes('shopify') || ecommerceSubtype === 'shopify') {
          platform = 'shopify';
        }
        
        if (!platform || !supportedConnectors.includes(platform)) return;
        
        // Skip inventory-only CSV imports
        if (isCSV && connection?.import_type === 'ecommerce' && !ecommerceSubtype) return;
        
        // Map to distinct display names
        if (isCSV && connection?.import_type === 'ecommerce') {
          if (platform === 'amazon') sources.push('Amazon CSV Import');
          else if (platform === 'shopify') sources.push('Shopify CSV Import');
        } else {
          if (platform === 'amazon') sources.push('Amazon Selling Partner');
          else if (platform === 'shopify') sources.push('Shopify');
        }
      });
      
      // Deduplicate (in case multiple CSV imports exist for same platform)
      const uniqueSources = [...new Set(sources)];
      sources.length = 0;
      sources.push(...uniqueSources);
      
      // Add "All" option if there are multiple sources
      if (sources.length > 1) {
        sources.unshift('All');
      }
    }
    
    return sources.length > 0 ? sources : ['No supported connections'];
  };

  const handleExport = async () => {
    if (!organisation?.connections || organisation.connections.length < 1) {
      toast({
        title: 'No Connections',
        description: 'No data connections available for export',
        variant: 'destructive'
      });
      return;
    }

    setIsExporting(true);
    
    try {
      // Prepare filters, removing dataSources and pagination from table filters
      const { dataSources: _tableDataSources, page: _page, pageSize: _pageSize, ...restFilters } = filters;
      // Map display names to backend filter values
      const dataSourceFilterMap: Record<string, string> = {
        'Amazon Selling Partner': 'amazon_selling_partner',
        'Amazon CSV Import': 'amazon_csv',
        'Shopify': 'Shopify',
        'Shopify CSV Import': 'shopify_csv',
      };
      const mappedSource = dataSourceFilterMap[exportSettings.dataSource] || exportSettings.dataSource;
      const exportFilters = exportSettings.dataSource === 'All' 
        ? restFilters 
        : { ...restFilters, dataSource: mappedSource };

      // Use background export for consistency and proper cost allocation
      const result = await startBackgroundExport({
        connectionId: `report-${activeReportTab}`,
        schema: 'reports',
        tableName: activeReportTab,
        filters: [],
        customSQL: JSON.stringify({
          reportType: activeReportTab,
          organisationId: organisation.id,
          filters: exportFilters
        })
      });

      if (result.success) {
        toast({
          title: 'Background Export Started',
          description: `Export started. You'll be notified when ready.`
        });
        
        // Switch to list view to show the export progress
        setView('list');
      } else {
        throw new Error(result.error || 'Failed to start background export');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: 'Export Failed',
        description: error instanceof Error ? error.message : 'Failed to export report',
        variant: 'destructive'
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Card className="fixed bottom-4 right-4 w-96 shadow-lg z-50" data-testid="reports-export-manager">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            {view === 'new' ? `Export ${activeReportTab === 'skus' ? 'Products' : 
                   activeReportTab === 'fixed-costs' ? 'Fixed Costs' : 'Sales History'}` : 'Recent Exports'}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant={view === 'new' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setView('new')}
              className="h-7 px-2"
              data-testid="toggle-new-export"
            >
              <Download className="h-3 w-3 mr-1" />
              New
            </Button>
            <Button
              variant={view === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setView('list')}
              className="h-7 px-2"
              data-testid="toggle-recent-exports"
            >
              <List className="h-3 w-3 mr-1" />
              List
            </Button>
            <Button onClick={onClose} variant="ghost" size="sm" data-testid="close-export-manager">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      {view === 'new' ? (
        <CardContent className="pt-0 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="data-source" className="text-sm font-medium">Source</Label>
            <Select 
              value={exportSettings.dataSource} 
              onValueChange={(value) => setExportSettings({ ...exportSettings, dataSource: value })}
            >
              <SelectTrigger id="data-source">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getAvailableDataSources().map((source) => (
                  <SelectItem key={source} value={source}>{source}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="format" className="text-sm font-medium">Format</Label>
            <Select 
              value={exportSettings.format} 
              onValueChange={(value) => setExportSettings({ ...exportSettings, format: value })}
            >
              <SelectTrigger id="format">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="xlsx">Excel (XLSX)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="filename" className="text-sm font-medium">Filename</Label>
            <Input
              id="filename"
              value={exportSettings.filename}
              onChange={(e) => setExportSettings({ ...exportSettings, filename: e.target.value })}
              placeholder="Enter filename"
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="include-headers"
              checked={exportSettings.includeHeaders}
              onChange={(e) => setExportSettings({ ...exportSettings, includeHeaders: e.target.checked })}
              className="rounded border-gray-300"
            />
            <Label htmlFor="include-headers" className="text-sm">Include column headers</Label>
          </div>

          <div className="flex gap-2 pt-2">
            <Button 
              onClick={handleExport} 
              disabled={isExporting}
              className="flex-1"
            >
              <Download className={`h-4 w-4 mr-2 ${isExporting ? 'animate-bounce' : ''}`} />
              {isExporting ? 'Exporting...' : 'Export Report'}
            </Button>
            <Button 
              onClick={onClose} 
              variant="outline"
            >
              Cancel
            </Button>
          </div>

          {filters.trailing12Months && (
            <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
              <Calendar className="h-3 w-3 inline mr-1" />
              Export will include trailing 12 months filter
            </div>
          )}
        </CardContent>
      ) : (
        <CardContent className="p-0">
          <ReportsExportList
            isOpen={true}
            onClose={() => setView('new')}
            organisationId={organisation.id}
          />
        </CardContent>
      )}
    </Card>
  );
};

export default ReportsExportManager;